import React, { useState } from "react";
import { Button, Card, Row, Col, Typography, Progress, Checkbox, Radio } from "antd";
import { SearchOutlined, ThunderboltOutlined } from "@ant-design/icons";
import PONumberTagInput from "./components/PONumberTagInput";
import ExistingDocsPanel from "./components/ExistingDocsPanel";
import ResultsPanel from "./components/ResultsPanel";
import { useAccount } from "./hooks/useAccount";

const { Text } = Typography;

export default function AccountPage() {
  const [lastAction, setLastAction] = useState<"check" | "process" | null>(null);

  const {
    poNumbers,
    setPoNumbers,
    allDocs,
    setAllDocs,
    statusOverride,
    setStatusOverride,
    checkingDocs,
    docInfo,
    processing,
    results,
    progress,
    handleCheckDocs,
    handleProcess,
  } = useAccount();

  const handleCheckDocsClick = async () => {
    setLastAction("check");
    await handleCheckDocs();
  };

  const handleProcessClick = async () => {
    setLastAction("process");
    await handleProcess();
  };

  return (
    <div style={{ paddingTop: 8 }}>
      {/* Options */}
      <Row gutter={[24, 12]} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Checkbox
            checked={allDocs}
            onChange={(e) => setAllDocs(e.target.checked)}
            disabled={processing || checkingDocs}
          >
            <Text strong>All Documents</Text>
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
              {allDocs
                ? "(Full flow: PO, SO, GRN, Invoice, EWayBill, Credits, ServiceInvoice, Package, Shipment...)"
                : "(Minimal: only PO, SO, Invoice & EWayBill)"}
            </Text>
          </Checkbox>
        </Col>
        <Col>
          <Text strong style={{ marginRight: 8 }}>Status:</Text>
          <Radio.Group
            value={statusOverride || "auto"}
            onChange={(e) =>
              setStatusOverride(e.target.value === "auto" ? null : e.target.value)
            }
            disabled={processing || checkingDocs}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="auto">Auto-detect</Radio.Button>
            <Radio.Button value="dispatched">Dispatched</Radio.Button>
          </Radio.Group>
        </Col>
      </Row>

      {/* Input Card */}
      <Card
        bordered
        style={{ marginBottom: 20, borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Text strong style={{ display: "block", marginBottom: 8, fontSize: 14 }}>
              PO Numbers
              {poNumbers.length > 0 && (
                <span style={{ color: "#1890ff", fontWeight: 400, marginLeft: 6 }}>
                  ({poNumbers.length} entered)
                </span>
              )}
            </Text>
            <PONumberTagInput
              value={poNumbers}
              onChange={setPoNumbers}
              disabled={processing || checkingDocs}
            />
            <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
              Paste or type PO numbers separated by commas or spaces. Status is auto-detected from delivery records.
            </Text>
          </Col>

          <Col span={24}>
            <Row gutter={12} justify="end">
              <Col>
                <Button
                  icon={<SearchOutlined />}
                  onClick={handleCheckDocsClick}
                  loading={checkingDocs}
                  disabled={!poNumbers.length || processing}
                  size="large"
                >
                  Check Existing Docs
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={handleProcessClick}
                  loading={processing}
                  disabled={!poNumbers.length || checkingDocs}
                  size="large"
                >
                  Process Documents
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        {progress && (
          <div style={{ marginTop: 16 }}>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
              Processing {progress.current} of {progress.total} POs...
            </Text>
            <Progress
              percent={Math.round((progress.current / progress.total) * 100)}
              status="active"
              format={() => `${progress.current}/${progress.total}`}
            />
          </div>
        )}
      </Card>

      {/* Existing Docs Panel */}
      {docInfo && lastAction === "check" && (
        <Card
          title="Existing Documents"
          bordered
          style={{ marginBottom: 20, borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        >
          <ExistingDocsPanel data={docInfo} />
        </Card>
      )}

      {/* Results Panel */}
      {results && lastAction === "process" && (
        <Card
          title="Creation Results"
          bordered
          style={{ borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        >
          <ResultsPanel data={results} />
        </Card>
      )}
    </div>
  );
}
